import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, MessageSquare, Sparkles } from "lucide-react";

/**
 * Dialog for sending a one-off message to an online operator.
 *
 * Presents two content sources:
 *   • Ready-made templates from Settings → «Έτοιμα Σχόλια»
 *     (persisted in ``quick_notes`` collection, fetched via
 *     ``GET /api/quick-notes``). One click sends immediately.
 *   • A free-text area for ad-hoc content that isn't worth turning
 *     into a permanent template.
 *
 * The dialog stays out of the way of the POS main flow — it renders
 * on top of everything else and dismisses on ``onClose``.
 */
export default function SendMessageDialog({ open, onOpenChange, target }) {
  const [templates, setTemplates] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Load templates once when the dialog opens so we don't burn a
  // request on every parent re-render.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingTemplates(true);
    http
      .get("/quick-notes")
      .then((r) => {
        if (cancelled) return;
        const rows = (r.data || []).filter((n) => n.is_active !== false);
        // Sort: most-used first, then display_order, then alphabetical.
        rows.sort((a, b) => {
          const u = (b.usage_count || 0) - (a.usage_count || 0);
          if (u) return u;
          const o = (a.display_order || 0) - (b.display_order || 0);
          if (o) return o;
          return (a.text || "").localeCompare(b.text || "", "el");
        });
        setTemplates(rows);
      })
      .catch(() => setTemplates([]))
      .finally(() => !cancelled && setLoadingTemplates(false));
    setText("");
    return () => { cancelled = true; };
  }, [open]);

  const send = useCallback(
    async (payloadText, template) => {
      const finalText = (payloadText || "").trim();
      if (!finalText) {
        toast.error("Γράψε ή διάλεξε ένα μήνυμα");
        return;
      }
      if (!target?.id) {
        toast.error("Λείπει ο παραλήπτης");
        return;
      }
      setBusy(true);
      try {
        const r = await http.post("/messages/send", {
          to_user_id: target.id,
          text: finalText,
          template_id: template?.id || null,
          template_label: template ? "Έτοιμο" : "Ad-hoc",
        });
        if (r.data?.broadcast) {
          const dc = r.data.delivered_count || 0;
          const tot = r.data.total_recipients || 0;
          toast.success(
            dc === tot
              ? `Στάλθηκε σε ${tot} χειριστές`
              : `Στάλθηκε σε ${dc}/${tot} χειριστές (οι υπόλοιποι θα το λάβουν όταν συνδεθούν)`,
          );
        } else if (r.data?.delivered) {
          toast.success(`Στάλθηκε στον ${target.name}`);
        } else {
          toast.info(`Θα το λάβει ο ${target.name} μόλις συνδεθεί`);
        }
        onOpenChange(false);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Απέτυχε η αποστολή");
      } finally {
        setBusy(false);
      }
    },
    [target, onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="text-blue-600" size={20} />
            Μήνυμα προς <span className="text-blue-700">{target?.name || "—"}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Διάλεξε ένα έτοιμο σχόλιο ή γράψε το δικό σου. Ο παραλήπτης
            θα το δει άμεσα στην οθόνη του.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Ready templates */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles size={14} className="text-amber-600" />
              <span className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
                Έτοιμα Σχόλια
              </span>
            </div>
            {loadingTemplates ? (
              <div className="text-xs text-slate-400 py-2">Φόρτωση...</div>
            ) : templates.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-2">
                Δεν υπάρχουν αποθηκευμένα σχόλια. Πρόσθεσε από «Ρυθμίσεις → Έτοιμα Σχόλια».
              </div>
            ) : (
              <div
                className="grid grid-cols-2 gap-1.5 max-h-[180px] overflow-y-auto pr-1"
                data-testid="send-message-templates"
              >
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={busy}
                    onClick={() => send(t.text, t)}
                    data-testid={`send-message-template-${t.id}`}
                    className="text-left text-xs px-2.5 py-2 rounded-md border border-slate-300 bg-slate-50 hover:bg-amber-50 hover:border-amber-400 active:scale-[0.98] transition-all disabled:opacity-50"
                  >
                    <div className="font-medium text-slate-800 leading-snug line-clamp-2">
                      {t.text}
                    </div>
                    {t.usage_count > 0 && (
                      <div className="text-[9px] text-slate-400 mt-1">
                        {t.usage_count}× χρήση
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Free text */}
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">
              Ή γράψε το δικό σου
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, 500))}
              placeholder="Πληκτρολόγησε μήνυμα..."
              rows={3}
              data-testid="send-message-textarea"
              className="resize-none"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-slate-400">
                {text.length}/500 χαρακτήρες
              </span>
              {text.length >= 500 && (
                <span className="text-[10px] text-red-600 font-bold">
                  Έφτασες το όριο
                </span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="send-message-cancel"
          >
            Άκυρο
          </Button>
          <Button
            onClick={() => send(text)}
            disabled={busy || !text.trim()}
            data-testid="send-message-submit"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Send size={14} className="mr-1" />
            {busy ? "Αποστολή..." : "Αποστολή"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
